import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Users, X, CircleNotch, LockSimple, UsersThree, Check } from "@phosphor-icons/react";

export default function ReportShareDialog({ report, onClose, onUpdated }) {
  const [admins, setAdmins] = useState(null);
  const [visibility, setVisibility] = useState(report.visibility || "private");
  const [selected, setSelected] = useState(new Set(report.viewer_admin_ids || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admins").then((r) => setAdmins(r.data.admins)).catch(() => setAdmins([]));
  }, []);

  const toggle = (id) => setSelected((p) => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/reports/${report.id}`, {
        visibility,
        viewer_admin_ids: Array.from(selected),
      });
      toast.success("Access updated");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const others = (admins || []).filter((a) => a.user_id !== report.created_by);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="report-share-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={busy ? undefined : onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-md flex-col rounded-sm border border-neutral-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#0055FF]/10"><Users size={17} weight="bold" className="text-[#0055FF]" /></div>
            <h2 className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">Manage access</h2>
          </div>
          <button data-testid="report-share-close" onClick={onClose} disabled={busy} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-50"><X size={18} weight="bold" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <p className="text-sm text-neutral-500">Choose which admins can view "<span className="font-semibold text-neutral-700">{report.title}</span>". Only you can edit or delete it.</p>

          <div className="space-y-2">
            <button data-testid="visibility-private" onClick={() => setVisibility("private")}
              className={`flex w-full items-start gap-3 rounded-sm border px-4 py-3 text-left transition-all duration-200 ${visibility === "private" ? "border-[#0055FF] bg-[#0055FF]/5" : "border-neutral-200 hover:border-neutral-300"}`}>
              <LockSimple size={18} weight="bold" className={visibility === "private" ? "text-[#0055FF]" : "text-neutral-400"} />
              <div>
                <p className="text-sm font-semibold text-[#0A0A0A]">Private</p>
                <p className="text-xs text-neutral-500">Only you and the admins you select below can view it.</p>
              </div>
            </button>
            <button data-testid="visibility-team" onClick={() => setVisibility("team")}
              className={`flex w-full items-start gap-3 rounded-sm border px-4 py-3 text-left transition-all duration-200 ${visibility === "team" ? "border-[#0055FF] bg-[#0055FF]/5" : "border-neutral-200 hover:border-neutral-300"}`}>
              <UsersThree size={18} weight="bold" className={visibility === "team" ? "text-[#0055FF]" : "text-neutral-400"} />
              <div>
                <p className="text-sm font-semibold text-[#0A0A0A]">All admins</p>
                <p className="text-xs text-neutral-500">Everyone on the admin team can view it.</p>
              </div>
            </button>
          </div>

          {visibility === "private" && (
            <div data-testid="admin-viewer-list">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">Admins with access ({selected.size} selected)</p>
              {admins === null ? (
                <div className="flex h-20 items-center justify-center"><CircleNotch size={20} className="animate-spin text-[#0055FF]" /></div>
              ) : others.length === 0 ? (
                <p className="rounded-sm bg-neutral-50 px-3 py-4 text-center text-sm text-neutral-400">No other admins to share with.</p>
              ) : (
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-sm border border-neutral-200 p-1">
                  {others.map((a) => {
                    const on = selected.has(a.user_id);
                    return (
                      <button key={a.user_id} data-testid={`admin-viewer-${a.user_id}`} onClick={() => toggle(a.user_id)}
                        className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-left hover:bg-neutral-50">
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border ${on ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-neutral-300"}`}>{on && <Check size={13} weight="bold" />}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-[#0A0A0A]">{a.name || a.email}</span>
                          <span className="block truncate text-xs text-neutral-500">{a.email}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button onClick={onClose} disabled={busy} className="rounded-sm border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">Cancel</button>
          <button data-testid="report-share-save" onClick={save} disabled={busy}
            className="inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0044CC] disabled:opacity-50">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <Check size={15} weight="bold" />} Save access
          </button>
        </div>
      </div>
    </div>
  );
}
